// In-browser video export: composites the source footage + board panels onto
// a canvas in real time and records it with MediaRecorder — hardware-encoded
// MP4 where the browser supports it (Chrome/Edge; WebM elsewhere), audio
// carried over via WebAudio. A 45s clip renders in ~45s. The ffmpeg render
// kit remains the path for long footage or frame-exact output.

import { splitGeometry } from './split.js'
import { exportGeometry } from './exportKit.js'

export function pickRecorderMime() {
  if (typeof MediaRecorder === 'undefined') return null
  const candidates = [
    'video/mp4;codecs="avc1.640028,mp4a.40.2"',
    'video/mp4;codecs=avc1',
    'video/mp4',
    'video/webm;codecs="vp9,opus"',
    'video/webm',
  ]
  return candidates.find((c) => MediaRecorder.isTypeSupported(c)) ?? null
}

// boards: [{ seg: {start, end}, canvas }] aligned with the segment windows.
// Returns { promise, cancel } — promise resolves { blob, extension } or
// rejects (Error('cancelled') on user cancel).
export function renderMovie({ src, duration, videoW, videoH, overlay, boards, onProgress }) {
  const mime = pickRecorderMime()
  if (!mime) {
    return { promise: Promise.reject(new Error('This browser cannot record video')), cancel() {} }
  }

  // Route audio through WebAudio so the render is silent but the track is
  // captured. Created first thing so the user-activation from the click is
  // still fresh.
  const audioCtx = new AudioContext()
  audioCtx.resume().catch(() => {})

  const video = document.createElement('video')
  video.src = src
  video.playsInline = true
  video.preload = 'auto'

  const sourceNode = audioCtx.createMediaElementSource(video)
  const audioDest = audioCtx.createMediaStreamDestination()
  sourceNode.connect(audioDest)

  const split = overlay.mode === 'split'
  const geometry = split
    ? splitGeometry({ share: overlay.split.share, videoW, videoH, pos: overlay.split.pos })
    : exportGeometry({ overlay, videoWidth: videoW, videoHeight: videoH })
  const canvas = document.createElement('canvas')
  canvas.width = split ? geometry.frameW : videoW
  canvas.height = split ? geometry.frameH : videoH
  const ctx = canvas.getContext('2d')

  const draw = () => {
    if (split) {
      ctx.fillStyle = '#000'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      const { crop, videoBand } = geometry
      ctx.drawImage(video, crop.x, crop.y, crop.w, crop.h, 0, videoBand.y, videoBand.w, videoBand.h)
    } else {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    }
    const t = video.currentTime
    const active = boards.find(({ seg }) => t >= seg.start && t < seg.end)
    if (active) {
      const x = split ? geometry.boardX : geometry.x
      const y = split ? geometry.boardY : geometry.y
      ctx.drawImage(active.canvas, x, y)
    }
  }

  const stream = new MediaStream([
    ...canvas.captureStream(30).getVideoTracks(),
    ...audioDest.stream.getAudioTracks(),
  ])
  const recorder = new MediaRecorder(stream, {
    mimeType: mime,
    videoBitsPerSecond: canvas.width * canvas.height >= 1_000_000 ? 12_000_000 : 7_000_000,
    audioBitsPerSecond: 128_000,
  })
  const chunks = []
  recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data)

  let cancelled = false
  let progressTimer = null
  let frameHandle = null

  const cleanup = () => {
    clearInterval(progressTimer)
    if (frameHandle != null && video.cancelVideoFrameCallback) {
      video.cancelVideoFrameCallback(frameHandle)
    }
    video.pause()
    video.removeAttribute('src')
    audioCtx.close().catch(() => {})
  }

  const promise = new Promise((resolve, reject) => {
    recorder.onstop = () => {
      cleanup()
      if (cancelled) {
        reject(new Error('cancelled'))
      } else {
        resolve({
          blob: new Blob(chunks, { type: mime.split(';')[0] }),
          extension: mime.startsWith('video/mp4') ? 'mp4' : 'webm',
        })
      }
    }
    recorder.onerror = (e) => {
      cleanup()
      reject(e.error ?? new Error('Recording failed'))
    }

    const loop = () => {
      draw()
      if (video.requestVideoFrameCallback) {
        frameHandle = video.requestVideoFrameCallback(loop)
      } else if (!video.ended && !cancelled) {
        frameHandle = null
        requestAnimationFrame(loop)
      }
    }

    video.addEventListener('ended', () => {
      draw()
      setTimeout(() => recorder.state !== 'inactive' && recorder.stop(), 150)
    })
    video.addEventListener('error', () => {
      cleanup()
      reject(new Error('The video failed to play during rendering'))
    })

    video.addEventListener(
      'canplay',
      () => {
        draw()
        recorder.start(500)
        video
          .play()
          .then(() => loop())
          .catch((err) => {
            cleanup()
            reject(err)
          })
        progressTimer = setInterval(() => {
          onProgress?.(duration ? Math.min(1, video.currentTime / duration) : 0)
        }, 250)
      },
      { once: true },
    )
  })

  return {
    promise,
    cancel() {
      cancelled = true
      if (recorder.state !== 'inactive') recorder.stop()
      else cleanup()
    },
  }
}
